import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, Send, Loader2, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";

const API = process.env.REACT_APP_BACKEND_URL;
const WA_URL = "https://wa.me/919110248636?text=" + encodeURIComponent("Namaste PurohithConnect, I need help with ");
const LBL = "block font-mukta text-sm font-semibold text-[#3B221B]";
const INP = "mt-1 w-full rounded-[12px] border border-[#A8722A]/30 bg-[#FBF6EC] px-4 py-2.5 font-mukta text-sm text-[#3B221B] outline-none transition-colors focus:border-[#B23A2B] focus:ring-2 focus:ring-[#B23A2B]/20";

const CHANNELS = [
  {
    key: "whatsapp", icon: MessageCircle, href: WA_URL, external: true,
    kn: "ವಾಟ್ಸಾಪ್‌ನಲ್ಲಿ ಚಾಟ್ ಮಾಡಿ", en: "Chat on WhatsApp", val: "+91 91102 48636",
    note: "Fastest reply — message us in one tap",
  },
  {
    key: "call", icon: Phone, href: "tel:+919110248636",
    kn: "ಕರೆ ಮಾಡಿ", en: "Call us", val: "+91 91102 48636",
    note: "Mon–Sat, 9 AM – 7 PM",
  },
  {
    key: "email", icon: Mail, href: "mailto:namaste@purohithconnect.com",
    kn: "ಇಮೇಲ್ ಮಾಡಿ", en: "Email us", val: "namaste@purohithconnect.com",
    note: "Admin: purohithconnect.admin@purohithconnect.com",
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useSeo({
    title: "Contact PurohithConnect — ಸಂಪರ್ಕಿಸಿ | Booking Help & Support in Bengaluru",
    description: "Need help booking a purohith or have a question? Contact PurohithConnect on WhatsApp +91 91102 48636, call us, email namaste@purohithconnect.com, or send a message — we'll reply quickly.",
    canonicalPath: "/contact",
    jsonLd: {
      "@context": "https://schema.org", "@type": "ContactPage",
      name: "Contact PurohithConnect",
      url: "https://purohithconnect.com/contact",
      mainEntity: {
        "@type": "Organization", name: "PurohithConnect",
        contactPoint: [
          { "@type": "ContactPoint", contactType: "customer support", telephone: "+91-9110248636", email: "namaste@purohithconnect.com", areaServed: "IN", availableLanguage: ["Kannada", "English"] },
          { "@type": "ContactPoint", contactType: "administration", email: "purohithconnect.admin@purohithconnect.com", areaServed: "IN" },
        ],
      },
    },
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed");
      setDone(true);
      toast.success("ಧನ್ಯವಾದಗಳು! ನಿಮ್ಮ ಸಂದೇಶ ತಲುಪಿದೆ · Thank you! Your message has reached us.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("ಕ್ಷಮಿಸಿ, ಕಳುಹಿಸಲಾಗಲಿಲ್ಲ · Could not send. Please try WhatsApp or email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-8 md:py-16" data-testid="contact-page">
        <div className="text-center">
          <p className="font-baloo text-base text-[#B23A2B]">ಸಂಪರ್ಕಿಸಿ</p>
          <h1 className="mt-1 font-eczar text-4xl font-bold text-[#3B221B] sm:text-5xl">Get in touch</h1>
          <p className="mx-auto mt-3 max-w-xl font-mukta text-base text-[#6B5044] md:text-lg">
            ಬುಕಿಂಗ್ ವೇಳೆ ಸಮಸ್ಯೆ ಅಥವಾ ಯಾವುದೇ ಮಾಹಿತಿ ಬೇಕೇ? ನಾವು ಸಹಾಯಕ್ಕೆ ಸಿದ್ಧ. · Facing a booking issue or need information? We're here to help.
          </p>
        </div>

        <div className="mt-10 grid gap-3 sm:grid-cols-3">
          {CHANNELS.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.key}
                href={c.href}
                {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={`contact-channel-${c.key}`}
                className="group flex flex-col rounded-[16px] border border-[#A8722A]/25 bg-[#FFFDF8] p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <span className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${c.key === "whatsapp" ? "bg-[#25D366]/15 text-[#128C3E]" : "bg-[#A8722A]/12 text-[#A8722A]"}`}>
                  <Icon size={20} />
                </span>
                <p className="mt-3 font-baloo text-sm text-[#B23A2B]">{c.kn}</p>
                <p className="font-eczar text-lg font-semibold text-[#3B221B]">{c.en}</p>
                <p className="mt-0.5 break-words font-mukta text-sm text-[#3B221B]">{c.val}</p>
                <p className="mt-1 font-mukta text-xs text-[#6B5044]">{c.note}</p>
              </a>
            );
          })}
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <div className="rounded-[18px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm md:p-8">
              <h2 className="font-eczar text-2xl font-semibold text-[#3B221B]">ಸಂದೇಶ ಕಳುಹಿಸಿ · Send us a message</h2>
              <p className="mt-1 font-mukta text-sm text-[#6B5044]">We'll reply to your email or phone as soon as we can.</p>
              {done ? (
                <div className="mt-6 flex flex-col items-center rounded-[14px] bg-[#EAF3EC] p-8 text-center" data-testid="contact-success">
                  <CheckCircle2 className="text-[#3E6B4F]" size={44} strokeWidth={1.5} />
                  <h3 className="mt-3 font-eczar text-xl font-semibold text-[#3B221B]">ನಿಮ್ಮ ಸಂದೇಶ ತಲುಪಿದೆ!</h3>
                  <p className="mt-1 font-mukta text-sm text-[#6B5044]">Your message has reached our team. We'll be in touch shortly.</p>
                  <button onClick={() => setDone(false)} data-testid="contact-send-another"
                    className="mt-4 font-mukta text-sm font-semibold text-[#B23A2B] hover:underline">Send another message</button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-5 space-y-4" data-testid="contact-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={LBL} htmlFor="c-name">ಹೆಸರು · Name</label>
                      <input id="c-name" required maxLength={120} value={form.name} onChange={set("name")} className={INP} placeholder="Your name" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <label className={LBL} htmlFor="c-phone">ಫೋನ್ · Phone</label>
                      <input id="c-phone" type="tel" maxLength={20} value={form.phone} onChange={set("phone")} className={INP} placeholder="+91 ..." data-testid="contact-phone-input" />
                    </div>
                  </div>
                  <div>
                    <label className={LBL} htmlFor="c-email">ಇಮೇಲ್ · Email</label>
                    <input id="c-email" type="email" required value={form.email} onChange={set("email")} className={INP} placeholder="you@example.com" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className={LBL} htmlFor="c-message">ಸಂದೇಶ · Message</label>
                    <textarea id="c-message" required maxLength={4000} rows={5} value={form.message} onChange={set("message")} className={INP} placeholder="How can we help? Tell us the occasion, date and area if it's a booking." data-testid="contact-message-input" />
                  </div>
                  <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-[#FFFDF8] transition-colors hover:bg-[#9a2f22] disabled:opacity-60">
                    {sending ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <><Send size={17} /> Send message</>}
                  </button>
                </form>
              )}
            </div>
          </div>

          <aside className="lg:col-span-2">
            <div className="rounded-[18px] border border-[#A8722A]/25 bg-[#F3EADA] p-6 md:p-7">
              <p className="font-baloo text-base text-[#B23A2B]">|| ಶ್ರೀ ||</p>
              <h3 className="mt-1 font-eczar text-xl font-semibold text-[#3B221B]">Prefer to talk now?</h3>
              <p className="mt-2 font-mukta text-sm text-[#6B5044]">For the fastest help with a booking, message us on WhatsApp — we reply in one tap.</p>
              <a href={WA_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-cta"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 font-mukta font-semibold text-white transition-colors hover:bg-[#1eb455]">
                <MessageCircle size={18} /> WhatsApp +91 91102 48636
              </a>
              <div className="mt-6 flex items-start gap-2 border-t border-[#A8722A]/20 pt-5 font-mukta text-sm text-[#6B5044]">
                <MapPin size={16} className="mt-0.5 text-[#A8722A]" />
                <span>Serving all of Bengaluru, Karnataka — from Malleshwaram to Whitefield, Jayanagar to Yelahanka.</span>
              </div>
            </div>
            <p className="mt-4 text-center font-mukta text-xs text-[#6B5044]">
              <Link to="/" className="hover:text-[#B23A2B]">← Back to home</Link>
            </p>
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
